<script setup>
import {computed, reactive, watch} from 'vue';
import {useAuthService} from '@/composables/useAuthService';
import SquashLevel from "@/components/players/SquashLevel.vue";
import PlayerCard from "@/components/players/PlayerCard.vue";
import AvatarImage from "@/components/images/AvatarImage.vue";

const props = defineProps({
  id: String,
  team: {
    type: Object,
    default: () => null,
  },
  size: {
    type: String,
    default: 'small',
  },
  expandable: {
    type: Boolean,
    default: true,
  },
  // Off unless a competition surface asks for it. Default-true meant ratings
  // appeared on club-wide lists and non-squash contexts, since nothing here
  // knows the sport — only the caller does. See useSquashLevels.
  showSquashLevel: {
    type: Boolean,
    default: false,
  },
  minimized: {
    type: Boolean,
    default: false
  },
  highlighted: {
    type: Boolean,
    default: false
  },
  highlightColor: {
    type: String,
    default: 'info'
  },
  responsive: {
    type: Boolean,
    default: false,
  },
  maxLines: {
    type: Number,
    default: null,
  },
  // Rendered handicap label ("+20", "Rec 15.3") — the caller resolves it from the
  // competition's handicap settings (useHandicaps.labelFor); null hides the chip.
  handicapLabel: {
    type: String,
    default: null,
  },
  // chip class from useHandicaps().chipClassFor — the one colour rule for handicap chips
  handicapTone: {type: String, default: 'tl-chip--brand'},
});

const {getItem, itemPending : loading, itemErrors : errors, item, hasItem} = useCrudApi('api/v2/teams');
const {user} = useAuthService();

watch(() => props.id, (newVal) => {
  if (newVal) {
    getItem(newVal);
  }
});
const currentItem = computed(() => {
  if (hasItem.value) {
    return item.value;
  }
  return props.team;
});
const state = reactive({
  dialog: false,
});
const handleClick = () => {
  if (props.expandable) {
    state.dialog = true;
  }
};

if (props.id) {
  getItem(props.id);
}
const style = computed(() => {
  return {
    cursor: props.expandable ? 'pointer' : 'default',
  };
});
const nameClass = computed(() => {
  if (props.responsive) return 'team-card-name--responsive';
  if (props.size === 'small') return 'team-card-name--small';
  if (props.size === 'medium') return 'team-card-name--medium';
  return 'team-card-name--large';
});
const avatarSize = computed(() => {
  // When responsive, always render at small size; CSS scales it up at lg+.
  // Avatar sits just slightly taller than the name text (≈1.3× the font size):
  // small 14px→20, medium 18px→24, large 24px→32.
  if (props.responsive) return 20;
  return props.size === 'small' ? 20 : (props.size === 'medium' ? 24 : 32);
});
const avatarClasses = computed(() => {
  if (props.responsive) {
    return props.minimized ? '' : 'mr-1 mr-lg-2';
  }
  return {
    'mr-1': !props.minimized && props.size === 'small',
    'mr-2': !props.minimized && props.size !== 'small',
  };
});
const nameClampClass = computed(() => {
  if (!props.maxLines) return null;
  if (props.maxLines === 1) return 'text-truncate';
  return `line-clamp-${props.maxLines}`;
});

// ── Team dialog (mirrors the Flutter TeamDialog bottom sheet) ──
const teamPlayers = computed(() => currentItem.value?.players ?? []);
const stackPlayers = computed(() => teamPlayers.value.slice(0, 3));
const extraCount = computed(() => Math.max(0, teamPlayers.value.length - 3));
// A singles team ALWAYS renders as its player (level, player dialog) — same rule
// as the app. An admin's display name still wins as the label, but only a real
// override: rows saved by older clients stored the player's own name as the
// "override", which must not count.
const singlePlayerDisplayName = computed(() => {
  const override = currentItem.value?.actualName?.trim();
  const player = currentItem.value?.singlePlayer;
  if (!override || !player) return null;
  const own = (player.fullName || `${player.firstName ?? ''} ${player.lastName ?? ''}`).trim();
  return override.toLowerCase() === own.toLowerCase() ? null : override;
});
const currentUserInTeam = computed(() => teamPlayers.value.some(p => p.userId === user.value?.id));

</script>

<template>
  <div v-if="currentItem" :class="{'team-card--responsive': responsive}">
    <!-- A single-player team renders as the player; an admin's display name
         (actualName override) replaces the label only. -->
    <template v-if="currentItem.singlePlayer">
      <player-card :highlight-color="highlightColor" :highlighted="highlighted" :minimized="minimized"
                   :player="currentItem.singlePlayer" :size="size"
                   :display-name="singlePlayerDisplayName"
                   :show-squash-level="showSquashLevel"
                   :handicap-label="handicapLabel" :handicap-tone="handicapTone"
                   :expandable="expandable" :max-lines="maxLines"/>
    </template>
    <template v-else>
      <div :style="style" @click="handleClick">
        <div class="d-flex align-center">
          <v-badge icon="mdi-arrow-bottom-left" :model-value="highlighted" :color="highlightColor" :floating="true">
            <v-tooltip :disabled="!minimized" :text="currentItem.name"
                       location="bottom">
              <template v-slot:activator="{ props }">
                <avatar-image v-bind="props" :size="avatarSize" :class="avatarClasses"
                              :filename="currentItem.imageFileName"
                              :fallback="currentItem.initials"/>
                <!-- flex-wrap: the handicap chip drops under the name when the column is tight (phones, matrices) -->
                <div v-if="!minimized" class="align-center d-flex align-content-center flex-wrap name-row" :class="nameClass">
                  <span :class="nameClampClass">{{ currentItem.name }}</span>
                  <span v-if="handicapLabel && !minimized" class="tl-chip tc-hcp" :class="handicapTone">{{ handicapLabel }}</span>
                  <div :class="responsive ? 'd-none d-lg-flex ml-2' : 'ml-2'"
                       v-if="(size === 'medium' || size === 'large') && showSquashLevel && currentItem.squashLevel > 0">
                    <squash-level size="x-small"
                                  :squash-level-id="currentItem.squashLevelId"
                                  :level="currentItem.squashLevel"/>
                  </div>
                </div>
              </template>
            </v-tooltip>
          </v-badge>
        </div>
        <v-dialog v-model="state.dialog" max-width="440">
          <div class="tl-sheet">
            <!-- head: close only -->
            <div class="tl-sheet-head">
              <v-btn :icon="true" variant="text" size="small" @click="state.dialog = false">
                <v-icon icon="mdi-close"/>
              </v-btn>
            </div>

            <!-- centred identity: overlapping avatar stack + team name -->
            <div class="tl-sheet-id">
              <div class="team-stack">
                <div
                    v-for="(p, i) in stackPlayers"
                    :key="p.id"
                    class="team-stack-item"
                    :style="{ marginLeft: i === 0 ? '0' : '-16px', zIndex: 10 - i }"
                >
                  <avatar-image :size="i === 0 ? 56 : 50" :filename="p.imageFileName" :fallback="p.initials"/>
                </div>
                <div v-if="extraCount" class="team-stack-item team-stack-extra" style="margin-left:-16px;">+{{ extraCount }}</div>
              </div>
              <div class="tl-sheet-name">{{ currentItem.name }}</div>
              <div v-if="currentUserInTeam" class="d-flex justify-center mt-3">
                <span class="tl-chip tl-chip--brand">Your team</span>
              </div>
            </div>

            <!-- members — tap a member for their profile -->
            <div class="tl-sheet-body">
              <div class="tl-sheet-label mb-2">Members · {{ teamPlayers.length }}</div>
              <div v-if="teamPlayers.length" class="tl-contact-card">
                <div v-for="player in teamPlayers" :key="player.id" class="tl-contact-row pa-2">
                  <player-card class="pa-3 w-100" :player="player" size="medium" :expandable="true" :show-squash-level="false"/>
                </div>
              </div>
              <div v-else class="tl-contact-card pa-4 text-medium-emphasis text-caption">No members in {{ currentItem.name }}</div>
              <div class="tl-sheet-note mt-3">Stats and messaging live on each member, so tap a member.</div>
            </div>

            <div class="pb-5"></div>
          </div>
        </v-dialog>
      </div>
    </template>
  </div>
  <div v-else-if="loading">
    Loading...
  </div>
  <div v-else-if="errors.length">
    <crud-error-display :errors="errors"/>
  </div>
</template>

<style scoped>
/* team dialog: overlapping avatar stack (3 faces + "+N") */
.team-stack {
  display: flex;
  align-items: center;
  justify-content: center;
}

.team-stack-item {
  border-radius: 50%;
  border: 3px solid rgb(var(--v-theme-surface));
  background: rgb(var(--v-theme-surface));
  flex-shrink: 0;
}

.team-stack-extra {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgb(var(--v-theme-light));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-charcoal));
}

.team-card-name--small { font-size: 14px; font-weight: 600; }
.team-card-name--medium { font-size: 18px; font-weight: 600; }
.team-card-name--large { font-size: 24px; font-weight: 600; }

/* Responsive variant: small by default, scales up at lg (1280px+) */
.team-card-name--responsive { font-size: 14px; font-weight: 600; }

@media (min-width: 1280px) {
  .team-card-name--responsive { font-size: 24px; }

  /* v-avatar and v-img use inline styles for size so !important is required */
  .team-card--responsive :deep(.v-avatar) {
    width: 32px !important;
    height: 32px !important;
  }
  .team-card--responsive :deep(.v-img) {
    width: 32px !important;
    height: 32px !important;
    max-height: 32px !important;
  }
}

/* Line clamp styles for multi-line text truncation with ellipsis */
.line-clamp-2,
.line-clamp-3,
.line-clamp-4,
.line-clamp-5 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-2 { -webkit-line-clamp: 2; }
.line-clamp-3 { -webkit-line-clamp: 3; }
.line-clamp-4 { -webkit-line-clamp: 4; }
.line-clamp-5 { -webkit-line-clamp: 5; }
.tc-hcp {
  font-size: 10px;
  font-weight: 700;
  padding: 0 6px;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* the name row: an 8px gap beside the name while the chip fits on the line, a 4px gap above it
   once the column is too tight and it wraps — the gap follows the wrap, not the screen width */
.name-row {
  column-gap: 8px;
  row-gap: 4px;
}
</style>
